'use client';

/* eslint-disable @typescript-eslint/no-unused-vars */

import { ParsedUrlQuery } from 'querystring';

import {
  FC,
  Fragment,
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useParams, usePathname } from 'next/navigation';

import { ListingFilterItemKey } from '@components/ui/filters/MultiSelectionFilter/types';
import { FilterContextType, useFiltersContext } from '@contexts/Filters';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import clean from '@utils/cleanObject';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { clearQueryParamsFilters } from '@components/views/hotel/utils';

import { ListingFiltersWrapper } from './ListingFiltersStyle';

/**
 * Creates a dynamic route with the slug and activeService parameters
 * @param slug string
 * @param activeService 'flight' | 'hotel' | 'tour' | 'cruise'
 * @returns string
 */
const generateUrl = (slug: string, activeService: ActiveServiceTypes) => {
  switch (activeService) {
    case 'hotel':
      return `${STATIC_ROUTES.hotelPathPrefix}/${slug}`;
    case 'tour':
      return `${STATIC_ROUTES.tourPathPrefix}/${slug}`;

    default:
      return `/${slug}`;
  }
};

type FilterKeyValueModel = {
  key: string;
  value: string;
  selected: boolean;
  count: number;
};
export type EntityFilter = {
  type?: string;
  title?: string;
  items: FilterKeyValueModel[];
};

export type ActiveServiceTypes = 'flight' | 'hotel' | 'tour' | 'cruise';

const getAvailableFilters = (entityFilters?: Array<EntityFilter>) => {
  return (
    entityFilters
      ?.map((ef) => ef.type)
      .reduce((acc, type) => {
        acc[type as string] = type;
        return acc;
      }, {} as Record<string, string | undefined>) ?? {}
  );
};

const useLocalState = ({
  initialKeyword = '',
  initialMinPrice,
  initialMaxPrice,
}: {
  initialKeyword: string;
  initialMinPrice: number | undefined;
  initialMaxPrice: number | undefined;
}) => {
  // ================================================
  // Local State variables to keep track of local data
  // ================================================
  const [keywords, setKeywords] = useState<string>(initialKeyword);
  const [minAccPriceValue, setMinAccPriceValue] = useState<number | undefined>(
    initialMinPrice
  );
  const [maxAccPriceValue, setMaxAccPriceValue] = useState<number | undefined>(
    initialMaxPrice
  );
  const [onlyAvailableHotels, setOnlyAvailableHotels] =
    useState<FilterKeyValueModel>();
  const [themes, setThemes] = useState<FilterKeyValueModel[]>();
  const [regions, setRegions] = useState<FilterKeyValueModel[]>();
  const [features, setFeatures] = useState<FilterKeyValueModel[]>();
  const [accommodationTaxTypes, setAccommodationTaxTypes] =
    useState<FilterKeyValueModel[]>();
  const [popularities, setPopularities] = useState<FilterKeyValueModel[]>();
  const [dates, setDates] = useState<FilterKeyValueModel[]>();
  const [departures, setDepartures] = useState<FilterKeyValueModel[]>();
  const [transportations, setTransportations] =
    useState<FilterKeyValueModel[]>();
  const [visas, setVisas] = useState<FilterKeyValueModel[]>();
  const [specialPeriods, setSpecialPeriods] = useState<FilterKeyValueModel[]>();
  const [boardTypes, setBoardTypes] = useState<FilterKeyValueModel[]>();
  const [freeCancellation, setFreeCancellation] =
    useState<FilterKeyValueModel>();
  const [stars, setStars] = useState<FilterKeyValueModel[]>();
  const [categories, setCategories] = useState<FilterKeyValueModel[]>();
  const [featureGroups, setFeatureGroups] = useState<EntityFilter[]>([]);

  return {
    featureGroups,
    setFeatureGroups,
    keywords,
    setKeywords,
    minAccPriceValue,
    setMinAccPriceValue,
    maxAccPriceValue,
    setMaxAccPriceValue,
    onlyAvailableHotels,
    setOnlyAvailableHotels,
    freeCancellation,
    setFreeCancellation,
    themes,
    setThemes,
    boardTypes,
    setBoardTypes,
    features,
    setFeatures,
    accommodationTaxTypes,
    setAccommodationTaxTypes,
    popularities,
    setPopularities,
    dates,
    setDates,
    departures,
    setDepartures,
    transportations,
    setTransportations,
    visas,
    setVisas,
    specialPeriods,
    setSpecialPeriods,
    regions,
    setRegions,
    stars,
    setStars,
    categories,
    setCategories,
  };
};

type LocalState = ReturnType<typeof useLocalState>;

export const useRouterPush = () => {
  const { push: nextPush } = useAppRouter();
  const query = useSearchParamsToQuery();
  const push: typeof nextPush = (args) => {
    return nextPush(args);
  };
  return { query, push };
};

export const getSelectedQueryParam = (
  query: ParsedUrlQuery,
  name: string
): number[] => {
  const value = query[name];

  if (!value) return [];

  const values = Array.isArray(value) ? value : value.split(',');
  const returnValue = values.map(Number).filter((num) => !isNaN(num));

  return returnValue;
};

const useUpdaters = ({
  setFilters,
  setFreeCancellation,
  activeService,
}: Pick<LocalState, 'setOnlyAvailableHotels' | 'setFreeCancellation'> & {
  activeService: ActiveServiceTypes;
} & Pick<FilterContextType, 'setFilters'>) => {
  const query = useSearchParamsToQuery();
  const params = useParams();
  const pathname = usePathname();
  const { push } = useAppRouter();

  const onRegionChecked = (checkedKeysValue: ListingFilterItemKey[]): void => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({ ...nextQuery, region: checkedKeysValue });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleSelectedThemes = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;

    const cleanQuery = clean({ ...nextQuery, theme: itemKeys as string[] });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleSelectedFeatures = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      feature: itemKeys as string[],
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleSelectedPopularities = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      popularity: itemKeys as string[],
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleAccommodationTaxTypes = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      accommodationTaxType: itemKeys as string[],
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleSelectedDates = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;

    const cleanQuery = clean({ ...nextQuery, date: itemKeys as string[] });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleSelectedFilterItems = (
    itemKeys: ListingFilterItemKey[],
    filterKey: string
  ) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({ ...nextQuery, [filterKey]: itemKeys });

    push({
      pathname: generateUrl(params?.slug as string, activeService),
      ...(Object.keys(cleanQuery).length ? { query: cleanQuery } : {}),
    });
  };

  const handleSelectedDepartures = (items: ListingFilterItemKey[]) =>
    handleSelectedFilterItems(items, 'departure');
  const handleSelectedTransportations = (items: ListingFilterItemKey[]) =>
    handleSelectedFilterItems(items, 'transportation');
  const handleSelectedVisas = (items: ListingFilterItemKey[]) =>
    handleSelectedFilterItems(items, 'visa');
  const handleSelectedSpecialPeriods = (items: ListingFilterItemKey[]) =>
    handleSelectedFilterItems(items, 'specialPeriod');

  const handleSelectedTripAdvisor = (point: string) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      point: point,
    });

    push({
      pathname: generateUrl(params?.slug as string, activeService),
      ...(Object.keys(cleanQuery).length !== 0 ? { query: cleanQuery } : {}),
    });
    setFilters((prevFilter) => ({
      ...prevFilter,
      selectedTripAdvisorItem: point,
    }));
  };

  const handleSelectedAccTypes = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;

    push({
      pathname: pathname ?? '',
      query: { ...nextQuery, boardType: itemKeys as string[] },
    });
  };
  const handleSingleSelectionFilter = (e: boolean, name: string) => {
    let queryParams;

    const { page, ...nextQuery } = query;

    if (name === 'status') {
      queryParams = { ...nextQuery, status: e };
    } else if (name === 'refundable') {
      queryParams = { ...nextQuery, refundable: e };
      setFilters((prevFilter) => ({
        ...prevFilter,
        freeCancellation: e,
      }));
      setFreeCancellation((prev) => ({
        ...(prev as FilterKeyValueModel),
        selected: e,
      }));
    }

    push({
      pathname: generateUrl(params?.slug as string, activeService),
      query: clean(queryParams),
    });
  };
  const handleKeywordsFilter = (keywords: string) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({ ...nextQuery, keywords });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleClearKeywordsFilter = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, keywords, ...nextQuery } = query;
    if (Object.keys(nextQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: clean({ ...nextQuery }),
      });
    }
  };

  const handlePriceFilter = (
    min: number | undefined,
    max: number | undefined
  ) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      priceMin: min,
      priceMax: max,
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  const handleClearAllFilters = (): void => {
    setFilters((val) => ({
      ...val,
      selectedRegions: [],
      freeCancellation: false,
      onlyAvailableProperties: false,
      hotelName: '',
      keywords: '',
      minAccommodationPrice: 0,
      maxAccommodationPrice: 0,
      selectedThemeItems: [],
      selectedFeaturesItems: [],
      selectedAccommodationTypes: [],
      selectedDateItems: [],
      selectedTripAdvisorItem: '',
      selectedStarItems: [],
    }));

    // Clear Query Params from Filters
    const { nextQuery } = clearQueryParamsFilters(query);
    push({
      pathname: generateUrl(params?.slug as string, activeService),
      query: nextQuery,
    });
  };

  const handleRemoveSpecificFilter = (key: string) => {
    const updatedQuery = query;
    delete updatedQuery[key];

    push({
      pathname: generateUrl(params?.slug as string, activeService),
      query: updatedQuery,
    });
  };

  const handleSelectedCategories = (itemKeys: ListingFilterItemKey[]) => {
    handleSelectedFilterItems(itemKeys, 'category');
  };

  const handleSelectedStars = (itemKeys: ListingFilterItemKey[]) => {
    const { page, ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      star: itemKeys as string[],
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
      });
    } else {
      push({
        pathname: generateUrl(params?.slug as string, activeService),
        query: cleanQuery,
      });
    }
  };

  return {
    handlePriceFilter,
    handleKeywordsFilter,
    handleSingleSelectionFilter,
    handleSelectedAccTypes,
    handleSelectedDates,
    handleSelectedDepartures,
    handleSelectedTransportations,
    handleSelectedVisas,
    handleSelectedSpecialPeriods,
    handleSelectedFeatures,
    handleSelectedThemes,
    onRegionChecked,
    handleClearKeywordsFilter,
    handleClearAllFilters,
    handleSelectedPopularities,
    handleAccommodationTaxTypes,
    handleSelectedTripAdvisor,
    handleRemoveSpecificFilter,
    handleSelectedStars,
    handleSelectedCategories,
  };
};

type Updaters = ReturnType<typeof useUpdaters>;

export type RenderParams = LocalState &
  Updaters &
  Pick<FilterContextType, 'filters'>;

export type FiltersBlockProps = {
  applicableFilters: EntityFilter[] | undefined;
  children: (config: RenderParams) => React.ReactElement;
  basePath?: string;
  noWrapper?: boolean;
  filterManagerRef?: MutableRefObject<RenderParams>;
  isWithShallow?: boolean;
  activeService: ActiveServiceTypes;
};

// ========================
// FilterBlock Starts here
// ========================
const FiltersBlock: FC<FiltersBlockProps> = ({
  applicableFilters,
  children,
  noWrapper,
  filterManagerRef,
  activeService,
}) => {
  const availableFilters = getAvailableFilters(applicableFilters);
  const query = useSearchParamsToQuery();
  const localState = useLocalState({
    initialKeyword: String(query.keywords ?? ''),
    initialMinPrice: parseFloat(query.priceMin as string),
    initialMaxPrice: parseFloat(query.priceMax as string),
  });
  const {
    setFreeCancellation,
    setKeywords,
    setOnlyAvailableHotels,
    setThemes,
    setBoardTypes,
    setAccommodationTaxTypes,
    setFeatures,
    setFeatureGroups,
    setPopularities,
    setDates,
    setDepartures,
    setTransportations,
    setVisas,
    setSpecialPeriods,
    setRegions,
    setMaxAccPriceValue,
    setMinAccPriceValue,
    setStars,
    setCategories,
  } = localState;
  // ================================================
  // Using React Hooks (useRouter , useContext)
  // ================================================
  const { filters, setFilters } = useFiltersContext();
  const updaters = useUpdaters({
    setFilters,
    setOnlyAvailableHotels,
    activeService,
    setFreeCancellation,
  });
  const renderParams = {
    ...localState,
    ...updaters,
    filters,
  };
  useImperativeHandle(filterManagerRef, () => renderParams);
  // ================================================
  // using the useEffect to fill the component on component load
  // ================================================

  const key = JSON.stringify(applicableFilters);
  useEffect(() => {
    if (!applicableFilters) return;
    const hasStatus = !!availableFilters['status'];
    const hasTheme = !!availableFilters['theme'];
    const refundable = !!availableFilters['refundable'];
    const hasBoardType = !!availableFilters['boardType'];
    const hasAccommodationTaxType = !!availableFilters['accommodationTaxType'];
    const hasRegion = !!availableFilters['region'];
    const hasDate = !!availableFilters['date'];
    const hasDeparture = !!availableFilters['departure'];
    const hasTransportation = !!availableFilters['transportation'];
    const hasVisa = !!availableFilters['visa'];
    const hasSpecialPeriod = !!availableFilters['specialPeriod'];
    const hasFeature = !!availableFilters['feature'];
    const hasPopularity = !!availableFilters['popularity'];
    const hasStar = !!availableFilters['star'];

    setFreeCancellation(
      refundable
        ? applicableFilters.filter((item) => item.type === 'refundable')[0]
            .items[0]
        : ({} as FilterKeyValueModel)
    );

    setFeatures(
      hasFeature
        ? applicableFilters?.filter((item) => item.type === 'feature')[0].items
        : []
    );

    setFeatureGroups(
      applicableFilters.filter(
        (item) => item.type === 'feature' && item.items?.length
      )
    );

    setAccommodationTaxTypes(
      hasAccommodationTaxType
        ? applicableFilters.filter(
            (item) => item.type === 'accommodationTaxType'
          )[0].items
        : []
    );

    setPopularities(
      hasPopularity
        ? applicableFilters?.filter((item) => item.type === 'popularity')[0]
            .items
        : []
    );

    setRegions(
      hasRegion
        ? applicableFilters?.filter((item) => item.type === 'region')[0].items
        : []
    );

    setDates(
      hasDate
        ? applicableFilters.filter((item) => item.type === 'date')[0].items
        : []
    );

    setDepartures(
      hasDeparture
        ? applicableFilters.find((item) => item.type === 'departure')?.items ||
            []
        : []
    );
    setTransportations(
      hasTransportation
        ? applicableFilters.find((item) => item.type === 'transportation')
            ?.items || []
        : []
    );
    setVisas(
      hasVisa
        ? applicableFilters.find((item) => item.type === 'visa')?.items || []
        : []
    );
    setSpecialPeriods(
      hasSpecialPeriod
        ? applicableFilters.find((item) => item.type === 'specialPeriod')
            ?.items || []
        : []
    );

    setStars(
      hasStar
        ? applicableFilters.find((item) => item.type === 'star')?.items ?? []
        : []
    );

    setCategories(
      applicableFilters.find((item) => item.type === 'category')?.items ?? []
    );

    setOnlyAvailableHotels(
      hasStatus
        ? applicableFilters.filter((item) => item.type === 'status')[0].items[0]
        : ({} as FilterKeyValueModel)
    );

    setThemes(
      hasTheme
        ? applicableFilters.filter((item) => item.type === 'theme')[0].items
        : []
    );

    setBoardTypes(
      hasBoardType
        ? applicableFilters.filter((item) => item.type === 'boardType')[0].items
        : []
    );

    const selectedRegion =
      applicableFilters
        ?.filter((item) => item.type === 'region')?.[0]
        ?.items?.filter((item) => item.selected === true) || [];
    const selectedRegionsKey = selectedRegion.map((item) => item.key);
    if (query.keywords) {
      setKeywords(String(query.keywords));
    } else {
      setKeywords('');
    }

    if (query.priceMin) {
      setMinAccPriceValue(Number(query.priceMin as string));
    } else {
      setMinAccPriceValue(undefined);
    }

    if (query.priceMax) {
      setMaxAccPriceValue(Number(query.priceMax as string));
    } else {
      setMaxAccPriceValue(undefined);
    }

    const onlyAvailableProperties = Boolean(query?.status);

    setFilters((prevFilters) => ({
      ...prevFilters,
      freeCancellation: refundable
        ? applicableFilters.filter((item) => item.type === 'refundable')[0]
            ?.items[0]?.selected
        : undefined,
      onlyAvailableProperties,
      selectedRegions: hasRegion ? selectedRegionsKey : [],
      regions: hasRegion
        ? applicableFilters?.filter((item) => item.type === 'region')[0]?.items
        : [],
      selectedThemeItems: applicableFilters
        ?.filter((item) => item.type === 'theme')[0]
        ?.items.filter((item) => item.selected === true),
      selectedAccommodationTypes: hasBoardType
        ? applicableFilters
            ?.filter((item) => item.type === 'boardType')[0]
            ?.items.filter((item) => item.selected === true)
        : undefined,
      selectedFeaturesItems: applicableFilters
        ?.filter((item) => item.type === 'feature')[0]
        ?.items.filter((item) => item.selected === true),
      selectedPopularitiesItems: applicableFilters
        ?.filter((item) => item.type === 'popularity')[0]
        ?.items.filter((item) => item.selected === true),
      selectedDateItems: applicableFilters
        ?.filter((item) => item.type === 'date')[0]
        ?.items.filter((item) => item.selected === true),
      maxAccommodationPrice: Number(query.priceMax as string) || undefined,
      minAccommodationPrice: Number(query.priceMin as string) || undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, key]);

  const Wrapper = noWrapper ? Fragment : ListingFiltersWrapper;
  return <Wrapper>{children(renderParams)}</Wrapper>;
};

export default FiltersBlock;
